import type { TreeNode } from '../../types/ui';
import { findClosestAncestorPath } from '../find-closest-ancestor-path/find-closest-ancestor-path';
import { findPropertyHostNode } from '../find-property-host-node/find-property-host-node';

/**
 * Resolve a form path (typically derived from `EntityOperationError.source.pointer`)
 * to the deepest navigable tree node the user can actually open via `?path=`.
 *
 * Backend pointers do not always line up 1:1 with the form's tree layout. Two
 * common mismatches:
 *
 * 1. The pointer points at a leaf inside a navigable node — e.g.
 *    `/data/attributes/flowTemplate/title` translates to
 *    `flowTemplate.title`, but the form only has a navigable node at
 *    `flowTemplate`. We walk up the dot path to find that ancestor.
 *
 * 2. The pointer is abbreviated relative to the form's schema layout — e.g.
 *    the backend returns `/data/attributes/campaignNodes` even though the
 *    form schema actually places it at
 *    `attributes.flowTemplate.campaignNodes`. There is no ancestor of
 *    `campaignNodes` in the nodeMap, so we fall back to scanning the
 *    navigable nodes for one whose sub-schema has a property with that
 *    leaf name.
 *
 * Returns the resolved node id, or `undefined` when no navigable target
 * can be inferred. Callers should treat `undefined` as "render the
 * error detail without a link" — falling back to the form root would
 * surface a misleading link that navigates somewhere unrelated to the
 * field named in the error.
 */
export const findNavigableFormPath = (
  formPath: string | undefined,
  nodeMap: Map<string, TreeNode> | undefined,
): string | undefined => {
  if (!nodeMap || nodeMap.size === 0 || !formPath) return undefined;

  // 1. Exact match or deepest navigable ancestor along the dot path.
  const ancestor = findClosestAncestorPath(formPath, (path) =>
    nodeMap.has(path),
  );
  if (ancestor) return ancestor;

  // 2. Property-name fallback — find a navigable node whose sub-schema has
  //    the leaf segment as a property. Useful when the backend pointer
  //    omits intermediate path segments.
  const segments = formPath.split('.');
  const leafSegment = segments[segments.length - 1];
  const propertyHost = findPropertyHostNode(leafSegment, nodeMap);
  return propertyHost?.id;
};
